// §34.13 (M66, D-495/D-502) — file offers: the vocabulary both ends share.
//
// **Send a copy, never a live share** (D-495). An offer moves bytes between two accounts on this
// server: the sender's file already *is* a content-addressed blob (D-047), so what travels is a
// reference and the recipient's accept is what turns it into a second `files` row charged to their
// own quota. Nothing here fetches, forwards or proxies anything — the two halves of an offer are
// two rows in the same database.
//
// The expiry arithmetic lives here rather than on the server because it is the one part of §34.13
// both ends compute: the server stamps `expiresAt` at create and enforces it lazily at every read,
// and the client needs the same number to say how long a card has left without asking.

/** §34.13 — the four states an offer can be in. The `CHECK` on the table is this list. */
export const OFFER_STATUSES = ['pending', 'accepted', 'declined', 'expired'] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

/**
 * One offer, as `GET /api/files/offers` returns it.
 *
 * `blob_sha` is deliberately **not** here. The recipient never needs it — accepting is a `POST` that
 * names the offer, and the server does the write — and handing it over would let anybody who was
 * ever offered a file ask `GET /api/blobs/:sha` for bytes they declined. The scoping on that route
 * ("present means present *for you*") is what makes the omission matter rather than being tidiness.
 */
export interface FileOffer {
  readonly id: string;
  /** The sender, as a ref (D-220). */
  readonly from: string;
  /** The sender's display name, resolved server-side — the card's `{user}`. */
  readonly fromName: string;
  /** The recipient, as a ref. */
  readonly to: string;
  readonly toName: string;
  /** The file's basename, which is what lands in the recipient's Downloads. */
  readonly name: string;
  readonly size: number;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly status: OfferStatus;
}

/**
 * §34.13 / §34.16 — when an offer created at `createdAt` stops being answerable.
 *
 * A function of two arguments rather than a constant plus arithmetic at four call sites: the create
 * route stamps it, the expiry sweep compares against it, and the unit test in M66.1 is about this
 * and nothing else. `days` comes from `WB_OFFER_EXPIRY_DAYS` — the caller passes it so neither half
 * of the app reaches into the other's config module.
 */
export function offerExpiresAt(createdAt: number, days: number): number {
  return createdAt + days * 24 * 60 * 60 * 1000;
}

/**
 * Has this offer run out by `now`? **Inclusive at the boundary**, so an offer never spends a
 * millisecond in a state where it is neither live nor expired.
 *
 * The comparison is `expiresAt <= now`, matching `inviteProblem`'s (§26.3) exactly. Two expiry
 * rules in one codebase that disagree about the instant they fire is the kind of difference that
 * only ever shows up in a test written against the wrong one.
 */
export function offerHasExpired(expiresAt: number, now: number): boolean {
  return expiresAt <= now;
}

/** Milliseconds left before `expiresAt`, floored at zero — what a card counts down. */
export function offerRemainingMs(expiresAt: number, now: number): number {
  return Math.max(0, expiresAt - now);
}

const UNITS = ['B', 'kB', 'MB', 'GB', 'TB'] as const;

/**
 * §34.13's `{size}`, for the two binding literals that carry one — the card title and the
 * out-of-space refusal.
 *
 * It lives in `shared` because **the shell renders one of those and Explorer renders the other**, and
 * §6.5 keeps `host/**` from reaching into an app for a helper. It is deliberately the *same* rule
 * Explorer's own `formatSize` uses — decimal units, one decimal below 100 — rather than a new one:
 * "12.3 MB" on the card and "12 MB" in the status line about the same file is the sort of difference
 * nobody reports and everybody notices. `web/tests/unit/apps/offer-copy.test.ts` asserts the two
 * agree over a table, so the duplication cannot drift even though it is still duplication.
 */
export function formatOfferSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const power = Math.min(UNITS.length - 1, Math.floor(Math.log10(bytes) / 3));
  const value = bytes / 1000 ** power;
  return `${value.toFixed(power === 0 || value >= 100 ? 0 : 1)} ${UNITS[power] ?? 'B'}`;
}
